import { ReactNode, useCallback, useState } from 'react';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';
import { AdminAction } from './PermissionGate';
import type { OverviewMetrics } from './overviewMetrics';

const log = createLogger('SystemStatusHeader');

/**
 * Normalised job counts displayed in the header pills, regardless of
 * which backend produced them.
 *
 * Lightning's `/health` exposes flat ``{active_jobs, pending_jobs, ...}``
 * fields; video's exposes a nested ``counts: {processing, pending,
 * completed, failed}`` object. Both shapes flow through this same
 * component once normalised in `AppShell` — and crucially, the counts
 * follow the active mode so switching apps swaps the numbers.
 */
export interface SystemStatusCounts {
  active: number;
  pending: number;
  completed: number;
  failed: number;
}

interface SystemStatusHeaderProps {
  counts: SystemStatusCounts | null;
  metrics?: OverviewMetrics | null;
  variant?: 'compact' | 'overview';
  /**
   * Which mini-app's health is being shown. Currently only used to gate
   * lightning-specific affordances (the orphan-jobs cleanup button hits
   * `/api/jobs/cleanup`, an endpoint the video and interior services
   * don't expose).
   */
  mode?:
    | 'lightning'
    | 'video'
    | 'interior'
    | 'ads'
    | 'mobile'
    | 'api'
    | 'cost'
    | 'qa'
    | 'release-notes';
}

type Tone = 'active' | 'pending' | 'complete' | 'failed' | 'duration' | 'duplicate';

interface ToneStyle {
  bg: string;
  text: string;
}

// Soft tint behind saturated text, so the four pills read as status colours
// against the cool-gray page without competing with the card content.
const TONE: Record<Tone, ToneStyle> = {
  active: { bg: 'bg-[#E8F4FA]', text: 'text-[#1489C9]' },
  pending: { bg: 'bg-warning-50', text: 'text-warning-700' },
  complete: { bg: 'bg-success-50', text: 'text-success-700' },
  failed: { bg: 'bg-danger-50', text: 'text-danger-700' },
  duration: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  duplicate: { bg: 'bg-purple-50', text: 'text-purple-700' },
};

interface StatCellProps {
  tone: Tone;
  icon: ReactNode;
  value: number;
  label: string;
  /**
   * Tailwind rounding class. Override when butting two cells together
   * to form a segmented pill (e.g. ``rounded-l-lg`` + ``rounded-r-lg``).
   */
  rounded?: string;
  cellClassName?: string;
  trailing?: ReactNode;
}

function StatCell({
  tone,
  icon,
  value,
  label,
  rounded = 'rounded-lg',
  cellClassName,
  trailing,
}: StatCellProps) {
  const t = TONE[tone];
  return (
    <div
      className={`relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-2 sm:px-2.5 py-2 sm:py-1 shadow-sm ${rounded} ${t.bg} ${t.text} ${cellClassName ?? ''}`}
    >
      <span className="hidden sm:flex items-center justify-center sm:[&>svg]:w-4 sm:[&>svg]:h-4">
        {icon}
      </span>
      <span className="text-base sm:text-sm font-bold tabular-nums leading-none">{value}</span>
      <span className="text-xs font-semibold leading-none">{label}</span>
      {trailing}
    </div>
  );
}

interface OverviewMetricProps {
  tone: Tone;
  icon: ReactNode;
  value: ReactNode;
  label: string;
  context: string;
  dividerClassName?: string;
  trailing?: ReactNode;
}

function OverviewMetric({
  tone,
  icon,
  value,
  label,
  context,
  dividerClassName = '',
  trailing,
}: OverviewMetricProps) {
  const t = TONE[tone];
  return (
    <div
      className={`group relative flex min-h-28 flex-col items-center justify-center px-3 py-5 text-center sm:min-h-32 ${dividerClassName}`}
    >
      <span className="flex items-center justify-center gap-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ring-4 ring-white shadow-sm ${t.bg} ${t.text} [&>svg]:h-5 [&>svg]:w-5`}
        >
          {icon}
        </span>
        <span className={`text-3xl font-semibold leading-none tracking-tight tabular-nums ${t.text}`}>
          {value}
        </span>
      </span>
      <div className={`mt-3 text-[10px] font-semibold uppercase tracking-[0.08em] ${t.text}`}>
        {label}
      </div>
      <div className="mt-1 truncate text-[10px] text-slate-400">{context}</div>
      {trailing}
    </div>
  );
}

function OverviewPrimaryMetric({
  value,
  label,
  context,
}: Pick<OverviewMetricProps, 'value' | 'label' | 'context'>) {
  return (
    <div className="flex min-h-28 items-center justify-center gap-5 border-b border-slate-200 px-6 py-5 sm:min-h-32 lg:justify-start lg:border-b-0 lg:border-r lg:px-8">
      <span className="text-5xl font-semibold leading-none tracking-[-0.05em] text-slate-950 tabular-nums sm:text-6xl">
        {value}
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase leading-tight tracking-[0.08em] text-slate-900">
          {label}
        </span>
        <span className="mt-1.5 block text-[10px] text-slate-400">{context}</span>
      </span>
    </div>
  );
}

const ICONS = {
  bolt: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  clock: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  checkCircle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  xCircle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  pulse: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h4l2.5-7 5 14 2.5-7h4" />
    </svg>
  ),
  duplicate: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
};

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
}

export function SystemStatusHeader({
  counts,
  metrics,
  mode = 'lightning',
  variant = 'compact',
}: SystemStatusHeaderProps) {
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ cleaned: number } | null>(null);

  const cleanupOrphanedJobs = useCallback(async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      // Lightning and mobile run the same server code, so the sweep exists on
      // both; route it at whichever one the header is currently showing.
      const response = await apiFetch('/api/jobs/cleanup', {
        method: 'POST',
        app: mode === 'mobile' ? 'mobile' : 'lightning',
      });
      if (response.ok) {
        const data = await response.json();
        log.info(`Cleanup completed: ${data.cleaned} orphaned jobs cleaned`);
        setCleanupResult({ cleaned: data.cleaned });
        setTimeout(() => setCleanupResult(null), 3000);
      } else {
        log.error(`Cleanup failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      log.error('Failed to cleanup orphaned jobs:', error);
    } finally {
      setCleaningUp(false);
    }
  }, [mode]);

  const showCleanupAffordance = mode === 'lightning' || mode === 'mobile';

  return (
    <div className="flex flex-col items-stretch gap-2 w-full">
      {counts && variant === 'overview' ? (
        <div className="space-y-3">
          <div>
            <h1 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900">
              Overview
            </h1>
            <p className="mt-1 text-[11px] text-slate-400">
              Real-time overview of generation activity
            </p>
          </div>

          <div className="grid w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft lg:grid-cols-[minmax(13rem,1.25fr)_minmax(0,6fr)]">
            <OverviewPrimaryMetric
              value={counts.completed}
              label="Complete"
              context="Last 24h"
            />
            <div className="grid min-w-0 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
              <OverviewMetric
                tone="active"
                icon={ICONS.bolt}
                value={counts.active}
                label="Active"
                context="Current queue"
                dividerClassName="lg:border-r lg:border-slate-200"
              />
              <OverviewMetric
                tone="pending"
                icon={ICONS.clock}
                value={counts.pending}
                label="Pending"
                context="Current queue"
                dividerClassName="lg:border-r lg:border-slate-200"
                trailing={
                  showCleanupAffordance &&
                  (counts.pending > 0 || counts.active > 0) && (
                    <AdminAction>
                      <button
                        onClick={cleanupOrphanedJobs}
                        disabled={cleaningUp}
                        className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-danger-600 text-[10px] text-white opacity-0 shadow-sm ring-2 ring-white transition-opacity hover:bg-danger-700 focus:opacity-100 group-hover:opacity-100"
                        title="Clear stuck jobs"
                      >
                        {cleaningUp ? '·' : '×'}
                      </button>
                    </AdminAction>
                  )
                }
              />
              <OverviewMetric
                tone="complete"
                icon={ICONS.checkCircle}
                value={metrics?.successRate == null ? '—' : `${Math.round(metrics.successRate)}%`}
                label="Success rate"
                context={metrics?.rangeLabel ?? 'Last 24h'}
                dividerClassName="lg:border-r lg:border-slate-200"
              />
              <OverviewMetric
                tone="duration"
                icon={ICONS.pulse}
                value={formatDuration(metrics?.p90Duration)}
                label="P90 duration"
                context={metrics?.rangeLabel ?? 'Last 24h'}
                dividerClassName="lg:border-r lg:border-slate-200"
              />
              <OverviewMetric
                tone="duplicate"
                icon={ICONS.duplicate}
                value={
                  metrics?.duplicatePercent == null
                    ? '—'
                    : `${metrics.duplicatePercent.toFixed(1)}%`
                }
                label="Duplicated"
                context={metrics?.rangeLabel ?? 'Last 24h'}
                dividerClassName="lg:border-r lg:border-slate-200"
              />
              <OverviewMetric
                tone="failed"
                icon={ICONS.xCircle}
                value={counts.failed}
                label="Failed"
                context="Last 24h"
              />
            </div>
          </div>
        </div>
      ) : counts ? (
        <div className="flex flex-row items-center justify-center gap-0 sm:justify-start sm:gap-3 sm:flex-wrap w-full">
          <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide text-slate-500">
            Current Queue
          </span>
          <div className="grid sm:inline-grid grid-cols-2 items-stretch w-1/2 sm:w-fit">
            <StatCell
              tone="active"
              icon={ICONS.bolt}
              value={counts.active}
              label="Active"
              rounded="rounded-l-lg"
              cellClassName={counts.active > 0 ? 'animate-pulse active-gold-glow' : undefined}
            />
            <StatCell
              tone="pending"
              icon={ICONS.clock}
              value={counts.pending}
              label="Pending"
              rounded="rounded-none sm:rounded-r-lg"
              cellClassName="group"
              trailing={
                showCleanupAffordance &&
                (counts.pending > 0 || counts.active > 0) && (
                  <AdminAction>
                    <button
                      onClick={cleanupOrphanedJobs}
                      disabled={cleaningUp}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-danger-600 text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-danger-700 flex items-center justify-center shadow-sm ring-2 ring-white"
                      title="Clear stuck jobs"
                    >
                      {cleaningUp ? '·' : '×'}
                    </button>
                  </AdminAction>
                )
              }
            />
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 sm:ml-auto w-1/2 sm:w-auto">
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide text-slate-500">
              Last 24h
            </span>
            <div className="grid sm:inline-grid grid-cols-2 items-stretch w-full sm:w-fit">
              <StatCell
                tone="complete"
                icon={ICONS.checkCircle}
                value={counts.completed}
                label="Complete"
                rounded="rounded-none sm:rounded-l-lg"
              />
              <StatCell
                tone="failed"
                icon={ICONS.xCircle}
                value={counts.failed}
                label="Failed"
                rounded="rounded-r-lg"
              />
            </div>
          </div>
        </div>
      ) : null}

      {cleanupResult && (
        <div className="px-3 py-1.5 bg-success-50 text-success-700 rounded-full text-xs flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Cleaned up {cleanupResult.cleaned} orphaned job{cleanupResult.cleaned !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
